export type PlatformCode =
  | "youtube"
  | "twitch"
  | "tiktok"
  | "douyin"
  | "facebook"
  | "instagram"
  | "twitter"
  | "steam_curator"
  | "ambassador" // Internal-only type (visible: false). Not selectable in campaign/roster editor.
  | "discord"
  | "telegram"
  | "bilibili"
  | "chzzk"
  | "afreeca"
  | "vk"; // Legacy type (visible: false). Kept for backward-compat with old DB records.

// "vk" is excluded from ChannelType because it is no longer a valid channel type.
export type ChannelType = Exclude<PlatformCode, "vk">;

/**
 * Shape of a single channel entry as returned by the v2 BE. Fields are all
 * optional because the exact subset present varies by endpoint (dashboard
 * list, panel detail, campaign stats, ...) — canonical source for the
 * near-identical inline `{ type?, title?, url?, thumbnail?, raw? }` shapes
 * that used to be hand-duplicated across lib/api and pages/*.
 */
export interface UserChannel {
  type?: ChannelType;
  channelId?: string;
  title?: string;
  url?: string;
  thumbnail?: string;
  country?: string;
  tokenError?: string;
  raw?: {
    verified?: boolean;
    isVerified?: boolean;
    broadcaster_type?: string;
    // Crawled account handle (e.g. Instagram/TikTok username) — see getChannelHandle().
    username?: string;
  };
}

/**
 * FontAwesome icon suffixes (without "fa-") for a platform's post-stat metrics.
 * Single source of truth for icons that vary by platform — e.g. "likes" is a
 * thumbs-up on YouTube/Facebook/Bilibili but a heart on Instagram/Twitter(X)/
 * TikTok/Douyin; "views" is an eye almost everywhere but "plays" on TikTok/
 * Douyin (bar-chart "status views" on Twitter/X).
 */
export interface ChannelMetricIcons {
  views?: string;
  likes?: string;
  comments?: string;
  shares?: string;
  replies?: string;
  dislikes?: string;
}

export interface ChannelPlatformMeta {
  code: PlatformCode;
  title: string;
  color: string;
  backgroundColor: string;
  iconScale?: number;
  iconOffsetX?: number;
  iconOffsetY?: number;
  visible: boolean;
  legacyIconClass: string;
  iconVariant: "fontawesome4" | "custom";
  socialKeys: string[];
  socialOrder?: number;
  filterKey?: string;
  followerField?: string;
  metricLabel?: string;
  profileUrlPattern?: RegExp;
  isChannelType?: boolean;
  metricIcons?: ChannelMetricIcons;
}

export const CHANNEL_PLATFORM_META: Record<PlatformCode, ChannelPlatformMeta> =
  {
    youtube: {
      code: "youtube",
      title: "YouTube",
      color: "#FF0000",
      backgroundColor: "#fff1f0",
      iconScale: 1.04,
      iconOffsetY: 0.01,
      visible: true,
      legacyIconClass: "fa-youtube-play",
      iconVariant: "fontawesome4",
      socialKeys: ["youtube_com"],
      socialOrder: 90,
      filterKey: "channelTypeYoutube",
      followerField: "youtubeSubscribers",
      metricLabel: "YouTube subscribers",
      profileUrlPattern:
        /(?:youtube\.com\/(?:channel\/|c\/|user\/|@))([^/?#]+)/,
      metricIcons: { views: "eye", likes: "thumbs-up", comments: "comment", dislikes: "thumbs-down" },
    },
    twitch: {
      code: "twitch",
      title: "Twitch",
      color: "#6441A4",
      backgroundColor: "#f4efff",
      iconScale: 1.02,
      iconOffsetY: 0.005,
      visible: true,
      legacyIconClass: "fa-twitch",
      iconVariant: "fontawesome4",
      socialKeys: ["twitch_tv"],
      socialOrder: 100,
      filterKey: "channelTypeTwitch",
      followerField: "twitchFollowers",
      metricLabel: "Twitch followers",
      profileUrlPattern: /twitch\.tv\/([^/?#]+)/,
      metricIcons: { views: "eye" },
    },
    tiktok: {
      code: "tiktok",
      title: "TikTok",
      color: "#010101",
      backgroundColor: "#efefef",
      iconScale: 1.06,
      iconOffsetY: 0.015,
      visible: true,
      legacyIconClass: "fa-music",
      iconVariant: "custom",
      socialKeys: ["tiktok_com"],
      socialOrder: 310,
      filterKey: "channelTypeTiktok",
      followerField: "tiktokFollowers",
      metricLabel: "TikTok followers",
      profileUrlPattern: /tiktok\.com\/@?([^/?#]+)/,
      metricIcons: { views: "play", likes: "heart", comments: "comment", shares: "share" },
    },
    douyin: {
      code: "douyin",
      title: "Douyin",
      color: "#4E0101",
      backgroundColor: "#fbeaea",
      iconScale: 1.06,
      iconOffsetY: 0.015,
      visible: true,
      legacyIconClass: "fa-music",
      iconVariant: "custom",
      socialKeys: ["douyin_com"],
      socialOrder: 315,
      filterKey: "channelTypeDouyin",
      followerField: "douyinFollowers",
      metricLabel: "Douyin followers",
      profileUrlPattern: /douyin\.com\/(?:user\/)?([^/?#]+)/,
      metricIcons: { views: "play", likes: "heart", comments: "comment", shares: "share" },
    },
    facebook: {
      code: "facebook",
      title: "Facebook",
      color: "#38589b",
      backgroundColor: "#eef3ff",
      iconScale: 1.16,
      iconOffsetY: 0.01,
      visible: true,
      legacyIconClass: "fa-facebook-square",
      iconVariant: "fontawesome4",
      socialKeys: ["facebook_com", "www_facebook_com"],
      socialOrder: 110,
      filterKey: "channelTypeFacebook",
      followerField: "facebookLikes",
      metricLabel: "Facebook likes",
      profileUrlPattern: /facebook\.com\/([^/?#]+)/,
      metricIcons: { views: "eye", likes: "thumbs-up", comments: "comment-o", shares: "retweet" },
    },
    instagram: {
      code: "instagram",
      title: "Instagram",
      color: "#bd1972",
      backgroundColor: "#fff0f7",
      iconScale: 1.02,
      iconOffsetY: 0.01,
      visible: true,
      legacyIconClass: "fa-instagram",
      iconVariant: "fontawesome4",
      socialKeys: ["instagram_com", "www_instagram_com"],
      socialOrder: 300,
      filterKey: "channelTypeInstagram",
      followerField: "instagramFollowers",
      metricLabel: "Instagram followers",
      profileUrlPattern: /instagram\.com\/([^/?#]+)/,
      metricIcons: { views: "eye", likes: "heart", comments: "comment-o" },
    },
    twitter: {
      code: "twitter",
      title: "Twitter",
      color: "#000000",
      backgroundColor: "#f2f2f2",
      iconScale: 0.96,
      iconOffsetY: 0.005,
      visible: true,
      legacyIconClass: "fa-twitter",
      iconVariant: "custom",
      socialKeys: ["twitter_com"],
      socialOrder: 200,
      filterKey: "channelTypeTwitter",
      followerField: "twitterFollowers",
      metricLabel: "Twitter followers",
      profileUrlPattern: /twitter\.com\/([^/?#]+)/,
      // views: "eye" — matches every other platform's view-count icon (this used to be
      // "bar-chart", the one outlier; Twitter's view count is the same kind of metric as
      // Facebook/Instagram/YouTube's, not a chart/analytics indicator).
      metricIcons: { views: "eye", likes: "heart", shares: "retweet", replies: "reply" },
    },
    steam_curator: {
      code: "steam_curator",
      title: "Steam curator",
      color: "#495057",
      backgroundColor: "#f3f4f6",
      iconScale: 0.95,
      iconOffsetY: 0.005,
      visible: true,
      legacyIconClass: "fa-steam-square",
      iconVariant: "fontawesome4",
      socialKeys: ["steamcommunity_com"],
      socialOrder: 500,
      filterKey: "channelTypeSteamCurator",
    },
    // Internal-only type used by full-access admins in the legacy editor (gc-mono CampaignForm.js).
    // Not included in DEFAULT_CHANNEL_TYPES and excluded from all UI selectors and display lists.
    // Kept in PlatformCode to preserve backward-compatibility with existing DB records.
    ambassador: {
      code: "ambassador",
      title: "Ambassador",
      color: "inherit",
      backgroundColor: "#f5f5f5",
      iconScale: 0.95,
      iconOffsetY: 0.005,
      visible: false,
      legacyIconClass: "fa-user-o",
      iconVariant: "fontawesome4",
      socialKeys: [],
    },
    discord: {
      code: "discord",
      title: "Discord",
      color: "#5865f2",
      backgroundColor: "#eef2ff",
      iconScale: 0.98,
      iconOffsetY: 0.005,
      visible: true,
      legacyIconClass: "fa-commenting",
      iconVariant: "custom",
      socialKeys: ["discord_gg"],
      socialOrder: 1000,
      filterKey: "channelTypeDiscord",
      metricLabel: "Discord members (online/total)",
      profileUrlPattern: /discord\.gg\/([^/?#]+)/,
    },
    telegram: {
      code: "telegram",
      title: "Telegram",
      color: "#229ed9",
      backgroundColor: "#eef9ff",
      iconScale: 1,
      iconOffsetY: 0.005,
      visible: true,
      legacyIconClass: "fa-paper-plane",
      iconVariant: "custom",
      socialKeys: ["t_me"],
      socialOrder: 1010,
      filterKey: "channelTypeTelegram",
      followerField: "telegramMembers",
      metricLabel: "Telegram members",
      profileUrlPattern: /t\.me\/([^/?#]+)/,
    },
    bilibili: {
      code: "bilibili",
      title: "Bilibili",
      color: "#00AEEC",
      backgroundColor: "#ecfbff",
      iconScale: 0.98,
      iconOffsetY: 0.005,
      visible: true,
      legacyIconClass: "icon-bilibili",
      iconVariant: "custom",
      socialKeys: ["space_bilibili_com", "bilibili_com"],
      socialOrder: 1100,
      filterKey: "channelTypeBilibili",
      followerField: "bilibiliFollowers",
      metricLabel: "Bilibili followers",
      profileUrlPattern: /space\.bilibili\.com\/([^/?#]+)/,
      // "views" is labeled "Plays" here (matches TikTok/Douyin's own "Plays" labeling), so it
      // uses the same "play" icon rather than "eye".
      metricIcons: { views: "play", likes: "thumbs-up", comments: "comment", shares: "retweet" },
    },
    chzzk: {
      code: "chzzk",
      title: "CHZZK",
      color: "#00ffa3",
      backgroundColor: "#1f1f1f",
      iconScale: 1,
      iconOffsetY: 0.005,
      visible: true,
      legacyIconClass: "icon-chzzk",
      iconVariant: "custom",
      socialKeys: ["chzzk_naver_com"],
      socialOrder: 90,
      filterKey: "channelTypeChzzk",
      followerField: "chzzkFollowers",
      metricLabel: "CHZZK followers",
      profileUrlPattern: /chzzk\.naver\.com\/([^/?#]+)/,
      metricIcons: { views: "eye" },
    },
    afreeca: {
      code: "afreeca",
      title: "SOOP",
      color: "#0545b1",
      backgroundColor: "#eef4ff",
      iconScale: 0.92,
      iconOffsetY: 0.005,
      visible: true,
      legacyIconClass: "icon-soop",
      iconVariant: "custom",
      socialKeys: ["afreecatv_com"],
      socialOrder: 120,
      filterKey: "channelTypeAfreeca",
      followerField: "afreecaFollowers",
      metricLabel: "Afreeca followers",
      profileUrlPattern: /afreecatv\.com\/([^/?#]+)/,
      // "likes" is not independently confirmed against the current SOOP VOD UI —
      // kept as the pre-existing default until someone verifies the actual "좋아요" icon.
      metricIcons: { views: "eye", likes: "thumbs-up", comments: "comment" },
    },
    vk: {
      code: "vk",
      title: "Vkontakte",
      color: "#4a76a8",
      backgroundColor: "#eef3ff",
      iconScale: 0.95,
      iconOffsetY: 0.005,
      visible: false,
      legacyIconClass: "fa-vk",
      iconVariant: "fontawesome4",
      socialKeys: ["vk_com"],
      socialOrder: 700,
      isChannelType: false,
    },
  };

export const channelTypes = Object.values(CHANNEL_PLATFORM_META)
  .filter(
    (meta): meta is ChannelPlatformMeta & { code: ChannelType } =>
      meta.isChannelType !== false,
  )
  .map((meta) => meta.code) as readonly ChannelType[];

export const channelTypesVisible: Record<ChannelType, boolean> =
  Object.fromEntries(
    channelTypes.map((type) => [type, CHANNEL_PLATFORM_META[type].visible]),
  ) as Record<ChannelType, boolean>;

/**
 * Selectable platform list for campaign/roster editors (ChannelTypePicker,
 * roster filters, ...). Deliberately excludes "ambassador" — an internal-only
 * legacy type (see CHANNEL_PLATFORM_META.ambassador) that must stay out of all
 * UI selectors — so this is a fixed list rather than derived from `channelTypes`.
 */
export const DEFAULT_CHANNEL_TYPES: ChannelType[] = [
  "youtube", "twitch", "tiktok", "douyin", "facebook", "instagram",
  "twitter", "steam_curator", "discord", "telegram", "bilibili", "chzzk", "afreeca",
];

export function getChannelPlatformMeta(
  type: string,
): ChannelPlatformMeta | undefined {
  return (
    CHANNEL_PLATFORM_META[type as PlatformCode] ??
    CHANNEL_PLATFORM_META[toGeneralPostType(type)]
  );
}

const POST_TYPE_COLORS: Record<string, string> = {
  twitch_stream: "#6441A4",
  twitch_video: "#a14340",
  twitch_clip: "#62a341",
};

const POST_TYPE_TITLES: Record<string, string> = {
  youtube_video: "YouTube video",
  youtube_short: "YouTube Short",
  youtube_stream: "YouTube stream",
  youtube_community_post: "YouTube community post",
  twitch_stream: "Twitch stream",
  twitch_video: "Twitch saved video/stream",
  twitch_clip: "Twitch clip",
  facebook_post: "Facebook post",
  facebook_video: "Facebook video",
  facebook_photo: "Facebook photo",
  facebook_stream: "Facebook stream",
  instagram_photo: "Instagram photo",
  instagram_video: "Instagram video",
  tiktok_video: "TikTok video",
  douyin_video: "Douyin video",
  twitter_post: "X (Twitter) post",
  chzzk_stream: "Chzzk stream",
  chzzk_video: "Chzzk video",
  afreeca_stream: "Soop stream",
  afreeca_video: "Soop video",
  bilibili_video: "Bilibili video",
  bilibili_stream: "Bilibili stream",
  discord_post: "Discord post",
};

export function getChannelTypeTitle(type: string): string {
  return (
    POST_TYPE_TITLES[type] ??
    getChannelPlatformMeta(type)?.title ??
    type.charAt(0).toUpperCase() + type.slice(1)
  );
}

export function getChannelTypeColor(type: string): string {
  return POST_TYPE_COLORS[type] ?? getChannelPlatformMeta(type)?.color ?? "inherit";
}

export function getChannelTypeBackgroundColor(type: string): string {
  return getChannelPlatformMeta(type)?.backgroundColor ?? "transparent";
}

export function getChannelTypeIcon(type: string): string {
  return getChannelPlatformMeta(type)?.legacyIconClass ?? "fa-user-o";
}

const METRIC_ICON_DEFAULTS: Required<ChannelMetricIcons> = {
  views: "eye",
  likes: "thumbs-up",
  comments: "comment",
  shares: "retweet",
  replies: "reply",
  dislikes: "thumbs-down",
};

/**
 * FontAwesome icon suffix (without "fa-") for one of a platform's post-stat
 * metrics — single source of truth so a future icon change only needs to
 * update `CHANNEL_PLATFORM_META`. Accepts a channel type ("youtube") or a
 * specific post type ("youtube_video") since `getChannelPlatformMeta`
 * resolves both. Falls back to a generic default per metric (e.g. "eye" for
 * views) for platforms/metrics with no override — genuinely mixed or
 * cross-platform contexts should keep using that default rather than
 * guessing a single platform.
 */
export function getChannelTypeMetricIcon(
  type: string,
  metric: keyof ChannelMetricIcons,
): string {
  return (
    getChannelPlatformMeta(type)?.metricIcons?.[metric] ??
    METRIC_ICON_DEFAULTS[metric]
  );
}

/** Convenience wrapper for the most commonly-varying metric — see `getChannelTypeMetricIcon`. */
export function getChannelTypeLikeIcon(type: string): string {
  return getChannelTypeMetricIcon(type, "likes");
}

export function getChannelTypeIconScale(type: string): number {
  return getChannelPlatformMeta(type)?.iconScale ?? 1;
}

export function getChannelTypeIconOffset(type: string): {
  x: number;
  y: number;
} {
  const meta = getChannelPlatformMeta(type);
  return {
    x: meta?.iconOffsetX ?? 0,
    y: meta?.iconOffsetY ?? 0,
  };
}

export function getChannelTypeSocialKeys(type: string): string[] {
  return getChannelPlatformMeta(type)?.socialKeys ?? [];
}

export function getChannelTypePrimarySocialKey(
  type: string,
): string | undefined {
  return getChannelTypeSocialKeys(type)[0];
}

export function getChannelTypeBySocialKey(
  key: string,
): PlatformCode | undefined {
  return Object.values(CHANNEL_PLATFORM_META).find((meta) =>
    meta.socialKeys.includes(key),
  )?.code;
}

export function getChannelTypeFilterKey(type: string): string | undefined {
  return getChannelPlatformMeta(type)?.filterKey;
}

export function getChannelTypeMetricLabel(type: string): string | undefined {
  return getChannelPlatformMeta(type)?.metricLabel;
}

export function getChannelTypeFollowerField(type: string): string | undefined {
  return getChannelPlatformMeta(type)?.followerField;
}

export function getChannelTypeProfilePattern(type: string): RegExp | undefined {
  return getChannelPlatformMeta(type)?.profileUrlPattern;
}

export function getChannelTypeSocialUrl(
  socials: Record<string, string | undefined> | undefined,
  type: string,
): string {
  if (!socials) return "";
  for (const key of getChannelTypeSocialKeys(type)) {
    const value = socials[key];
    if (typeof value === "string" && value.trim()) return value;
  }
  return "";
}

export function getOrderedSocialPlatformEntries(): Array<{
  type: PlatformCode;
  key: string;
  weight: number;
}> {
  return Object.values(CHANNEL_PLATFORM_META)
    .filter((meta) => meta.socialOrder != null && meta.socialKeys.length > 0)
    .map((meta) => ({
      type: meta.code,
      key: meta.socialKeys[0],
      weight: meta.socialOrder ?? Number.MAX_SAFE_INTEGER,
    }))
    .sort((a, b) => a.weight - b.weight);
}

export function getFilterableChannelPlatformEntries(): Array<{
  type: ChannelType;
  filterKey: string;
}> {
  return channelTypes
    .map((type) => ({
      type,
      filterKey: getChannelTypeFilterKey(type) ?? "",
    }))
    .filter(
      (entry): entry is { type: ChannelType; filterKey: string } =>
        entry.filterKey.length > 0,
    );
}

/* ──────────────────── Post-type utilities ──────────────────── */

/** 플랫폼 표시 순서 (탭·정렬용) */
export const PLATFORM_DISPLAY_ORDER: readonly PlatformCode[] = [
  "youtube",
  "twitch",
  "instagram",
  "tiktok",
  "douyin",
  "twitter",
  "facebook",
  "bilibili",
  "chzzk",
  "afreeca",
  "discord",
] as const;

/**
 * 구체적 post type (youtube_short, twitch_stream 등) → 일반 플랫폼 코드로 변환.
 * 알 수 없는 값은 'youtube' 반환.
 */
export function toGeneralPostType(postType?: string): PlatformCode {
  if (!postType) return "youtube";
  const prefix = postType.split("_")[0].toLowerCase();
  if (prefix in CHANNEL_PLATFORM_META) return prefix as PlatformCode;
  return "youtube";
}

/** 스트리밍 타입 여부 (twitch_stream, chzzk_stream, afreeca_stream 등) */
export function isStreamPostType(type?: string): boolean {
  if (!type) return false;
  return (
    type.endsWith("_stream") || ["twitch", "chzzk", "afreeca"].includes(type)
  );
}

/**
 * Advertising-type id 또는 label → 플랫폼 코드 변환.
 * 서버 partnership 값(full, short, pre_roll, tweet 등)과 UI 표시 label 모두 지원.
 */
const AD_TYPE_TO_PLATFORM: Record<string, PlatformCode> = {
  // youtube variants
  youtube: "youtube",
  youtube_short: "youtube",
  youtube_video: "youtube",
  youtube_stream: "youtube",
  youtube_community_post: "youtube",
  pre_roll: "youtube",
  full: "youtube",
  short: "youtube",
  integration: "youtube",
  stream_highlight: "youtube",
  community_post: "youtube",
  // twitch
  twitch: "twitch",
  twitch_vod: "twitch",
  twitch_stream: "twitch",
  vod: "twitch",
  stream: "twitch",
  // twitter/x
  twitter: "twitter",
  tweet: "twitter",
  twitter_post: "twitter",
  // others
  tiktok: "tiktok",
  douyin: "douyin",
  instagram: "instagram",
  instagram_post: "instagram",
  instagram_reel: "instagram",
  instagram_story: "instagram",
  facebook: "facebook",
  facebook_post: "facebook",
  facebook_video: "facebook",
  facebook_reel: "facebook",
  chzzk: "chzzk",
  chzzk_video: "chzzk",
  chzzk_stream: "chzzk",
  bilibili: "bilibili",
  bilibili_video: "bilibili",
  afreeca: "afreeca",
  afreeca_video: "afreeca",
  afreeca_stream: "afreeca",
  discord: "discord",
};

export function adTypeToPlatform(
  value: string | null | undefined,
  label?: string,
): PlatformCode | null {
  // `value` can be nullish at runtime (e.g. an advertising type from envs with no id), so guard the
  // value-based matching to avoid a `.toLowerCase()` crash — the label path below still runs.
  if (value) {
    const normalized = value.toLowerCase().replace(/[-]/g, "_");
    const fromValue =
      AD_TYPE_TO_PLATFORM[normalized] ?? AD_TYPE_TO_PLATFORM[value];
    if (fromValue) return fromValue;

    // value prefix 매칭
    const m = value.match(
      /^(youtube|twitch|tiktok|douyin|instagram|twitter|facebook|chzzk|bilibili|afreeca)/i,
    );
    if (m) return m[1].toLowerCase() as PlatformCode;
  }

  // label 기반 매칭
  if (label) {
    const l = label.toLowerCase();
    if (l.startsWith("yt ") || l.includes("youtube")) return "youtube";
    if (l.includes("twitch")) return "twitch";
    if (l.includes("tweet") || l.includes("twitter") || l.includes(" x "))
      return "twitter";
    if (l.includes("tiktok")) return "tiktok";
    if (l.includes("douyin")) return "douyin";
    if (l.includes("instagram")) return "instagram";
    if (l.includes("facebook")) return "facebook";
    if (l.includes("chzzk")) return "chzzk";
    if (l.includes("bilibili")) return "bilibili";
    if (l.includes("stream") && !l.includes("youtube")) return "twitch";
  }

  return null;
}
